// Canonical Web business-role normalization.
//
// IDENTITY-ONLY: this maps representation variants (case / whitespace / synonyms) to the
// canonical lowercase role tokens the Web guards compare against. It NEVER grants, elevates,
// or loosens permissions — an unknown role is returned trimmed + lowercased, not mapped to
// anything privileged. Fixes the class of bug where a staff record stored "Admin" (capitalized)
// failed case-sensitive guards like requiredRoles.includes(user.role).
const ROLE_ALIASES = {
  admin: 'admin',
  administrator: 'admin',
  'group administrator': 'admin',
  // group_administrator is the active Administrator SUPERSET token used by the iOS Business app
  // (app/(tabs)/index.tsx normalizes admin/administrator -> group_administrator) and recognized by the
  // backend (operator_analytics) — an Administrator alias, mapped to admin like the space form above.
  group_administrator: 'admin',
  'group-administrator': 'admin',
  manager: 'manager',
  operator: 'operator',
  analyst: 'analyst',
  director: 'director',
  executive: 'executive',
  it: 'it',
  'it/integration': 'it',
  'it-integration': 'it',
  it_integration: 'it',
  customer: 'customer',
  super_admin: 'super_admin',
  superadmin: 'super_admin',
  'super admin': 'super_admin',
};

export function normalizeBusinessRole(role) {
  if (!role || typeof role !== 'string') return '';
  const key = role.trim().toLowerCase();
  return ROLE_ALIASES[key] || key;
}

// Notification management (Push Notification Center). MUST mirror the backend Slice A policy
// (api/notification_routes.py NOTIFY_SEND_ROLES / read gate = admin/administrator/manager/executive/director).
// After normalization 'administrator' collapses to 'admin'. operator/analyst/it/customer/unknown/null
// are NOT allowed — this only HIDES a UI surface the backend already 403s; it never grants access.
export const NOTIFICATION_MANAGE_ROLES = ['admin', 'manager', 'executive', 'director'];

export function canManageNotifications(role) {
  return NOTIFICATION_MANAGE_ROLES.includes(normalizeBusinessRole(role));
}

// Ticket read authorization. MUST mirror the backend guard
// (api/business_route_guard.py TICKET_READ_ROLES = operator/admin/administrator/manager/
// analyst/director/executive). After normalization 'administrator' collapses to 'admin'.
// IT/Integration ('it') is intentionally NOT a ticket-read role — the same posture the backend
// enforces with a 403. This helper lets the Web HIDE ticket surfaces (e.g. the sidebar
// "Active Tickets" count) for unauthorized roles so we never issue a predictably-forbidden
// request; it never grants access the backend would deny.
export const TICKET_READ_ROLES = ['operator', 'admin', 'manager', 'analyst', 'director', 'executive'];

export function canReadTickets(role) {
  return TICKET_READ_ROLES.includes(normalizeBusinessRole(role));
}

// Scheduling authorization. MUST mirror the backend guards
// (api/scheduling_routes.py: GET /config => SCHED_BOOK_ROLES; PUT /config => SCHED_CONFIG_ROLES).
// After normalization 'administrator' collapses to 'admin'. IT/Integration ('it') is NOT a
// scheduling role in the authoritative product model — the backend 403s it on both read and write.
// These helpers let the Web hide scheduling nav/surfaces for unauthorized roles so we never issue a
// predictably-forbidden request; they never grant access the backend would deny.
// OPERATIONAL USAGE = admin/administrator(→admin)/manager/operator ONLY. analyst/director/executive/it are
// DENIED by the backend (SCHED_BOOK_ROLES) — mirror that here so the Web hides scheduling for them.
export const SCHEDULING_READ_ROLES = ['operator', 'admin', 'manager'];
export const SCHEDULING_CONFIG_ROLES = ['admin', 'manager'];

export function canReadScheduling(role) {
  return SCHEDULING_READ_ROLES.includes(normalizeBusinessRole(role));
}

// Initial profile MATERIALIZATION (POST /api/v1/billing/me/scheduling/configure) is guarded by the
// backend BILLING_ADMIN_ROLES, which is NARROWER than the scheduling-config roles above: a manager may
// edit an existing scheduling config but may NOT perform the first profile-driven setup. Mirroring the
// wider set here would show a control that 403s. `super_admin` is deliberately omitted: it is not a
// tenant management role (models/roles.py) and can never be a Business membership role on this plane.
export const SCHEDULING_PROFILE_SETUP_ROLES = ['admin', 'administrator', 'owner', 'billing_admin'];

export function canSetUpSchedulingFromProfile(role) {
  return SCHEDULING_PROFILE_SETUP_ROLES.includes(normalizeBusinessRole(role));
}

export function canConfigureScheduling(role) {
  return SCHEDULING_CONFIG_ROLES.includes(normalizeBusinessRole(role));
}

// DR-017 MFA policy governance. MUST mirror the backend guard
// (api/mfa_routes.py MFA_POLICY_ADMIN_ROLES = admin/administrator/it/integration/it_integration).
// Only Admin and IT/Integration manage the TENANT MFA policy; every other role has personal MFA only.
// This helper only decides whether to SHOW the tenant policy panel — the backend independently enforces
// role + tenant + entitlement on every policy route.
export const MFA_POLICY_ADMIN_ROLES = ['admin', 'it'];

export function canManageMfaPolicy(role) {
  return MFA_POLICY_ADMIN_ROLES.includes(normalizeBusinessRole(role));
}

// D15 — escalation MANAGEMENT authority. Mirrors the backend canonical MANAGEMENT_ROLES
// (models/roles.py: admin/administrator/manager/director/executive; super_admin excluded). After
// normalization 'administrator' and 'group_administrator' collapse to 'admin', so a valid Admin alias is
// never locked out. This is deliberately BROADER than Solution Bank approval (which is admin/manager only);
// it only decides whether the Web shows management escalation controls — the backend independently enforces
// role + tenant on every escalation route.
export const ESCALATION_MANAGE_ROLES = ['admin', 'manager', 'director', 'executive'];

export function canManageEscalations(role) {
  return ESCALATION_MANAGE_ROLES.includes(normalizeBusinessRole(role));
}

// GOV-7 fleet administration — mirrors backend MANAGEMENT_ROLES (models/roles.py). Presentational refinement
// ONLY: the authoritative gate is the server eligibility projection (vehicle/tracker_management_allowed) + the
// backend /fleet routes, which 403 regardless. Never gate fleet UI on role alone.
export const FLEET_MANAGE_ROLES = ['admin', 'administrator', 'manager'];

export function canManageFleet(role) {
  return FLEET_MANAGE_ROLES.includes(normalizeBusinessRole(role));
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// CURRENT-TENANT ROLE PROJECTION (ADM-01)
//
// Authorization-bearing Business controls were gated on `user.role` from CRMAuthContext, which is
// seeded from the role SELECTED ON THE LOGIN FORM (Login.jsx `role: credentials.role`). That value is
// not server-derived and not tenant-scoped, and the `|| currentBusiness?.role` fallback beside it is
// dead — BusinessContext's business object carries no `role` field at all. So an Administrator of
// FJON79W1 could have the Invite Staff control hidden entirely (runtime-proven: NOT_IN_DOM).
//
// This resolves the role from the CURRENTLY SELECTED tenant's own membership roster — the rows the
// page already holds from GET /api/v1/billing/staff/by-brand/{brand_id} (tenant-scoped and
// membership-enforced server-side). It adds no fetch, no store, no context and no endpoint, and it
// is a PROJECTION only: the backend remains the authorization authority and still answers 403.
//
// It FAILS CLOSED. Roster data is imperfect on staging — firebase_uid is present on only 10 of 14
// active FJON79W1 rows, and two active rows share an email — so:
//   1. exact firebase_uid match is primary (unique wherever present)
//   2. email is a fallback ONLY when it yields exactly ONE active row in THIS tenant
//   3. zero matches, ambiguous matches, missing roster or missing tenant → '' (control hidden)
// Never "pick first", never pick the most privileged row, never look in another tenant. Picking an
// arbitrary row is precisely the identity-only resolution defect PMT1 already forbids.
export function currentTenantRole(identity, roster, brandId) {
  if (!brandId || !Array.isArray(roster) || roster.length === 0) return '';
  const uid = String((identity && identity.firebaseUid) || '').trim();
  const email = String((identity && identity.email) || '').trim().toLowerCase();
  if (!uid && !email) return '';

  // Only ACTIVE rows, and only rows belonging to the tenant in hand. The roster is already
  // tenant-scoped by the server; the brand check is belt-and-braces so a stale roster from a
  // previous tenant selection can never answer for the current one.
  const rows = roster.filter((r) => {
    if (!r) return false;
    if (String(r.status || 'active').toLowerCase() === 'inactive') return false;
    const rb = r.brand_id || r.brandId;
    return !rb || String(rb) === String(brandId);
  });
  if (rows.length === 0) return '';

  if (uid) {
    const byUid = rows.filter((r) => String(r.firebase_uid || '').trim() === uid);
    if (byUid.length === 1) return String(byUid[0].role || '').trim();
    if (byUid.length > 1) return '';            // ambiguous → fail closed, never choose
  }

  if (email) {
    const byEmail = rows.filter((r) => String(r.email || '').trim().toLowerCase() === email);
    if (byEmail.length === 1) return String(byEmail[0].role || '').trim();
  }

  return '';
}

export default normalizeBusinessRole;
